import { useEffect, useMemo, useState } from "react";
import { Camera, Clock3, FileCheck2, LockKeyhole, ShieldCheck, X } from "lucide-react";
import { listEvidenceClips } from "../api";
import type { EvidenceClip, Vehicle } from "../types";

function statusLabel(status: string) {
  if (status === "ready_demo") return "LISTO · DEMO";
  if (status === "ready") return "EVIDENCIA LISTA";
  if (status === "failed") return "ERROR DE CAPTURA";
  return "SOLICITADO";
}

export function EvidencePanel({
  open,
  onClose,
  vehicles,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
}) {
  const [clips, setClips] = useState<EvidenceClip[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      setError("");
      listEvidenceClips()
        .then(setClips)
        .catch((reason) => setError(reason instanceof Error ? reason.message : "No fue posible cargar evidencia"));
    };
    refresh();
    window.addEventListener("gsf:evidence-updated", refresh);
    return () => window.removeEventListener("gsf:evidence-updated", refresh);
  }, [open]);

  const ready = useMemo(
    () => clips.filter((clip) => clip.status === "ready" || clip.status === "ready_demo").length,
    [clips],
  );

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="admin-panel evidence-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="evidence-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">CADENA DE CUSTODIA DIGITAL</span>
            <h2 id="evidence-title">Clips automáticos de eventos</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar evidencia"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Camera size={17} /><b>{clips.length}</b> clips</span>
          <span><FileCheck2 size={17} /><b>{ready}</b> disponibles</span>
          <span><LockKeyhole size={17} /><b>{clips.filter((clip) => clip.locked).length}</b> bloqueados</span>
        </section>
        <div className="admin-panel__body evidence-panel__body">
          <section>
            <div className="admin-section-title">
              <span className="eyebrow">EVIDENCIA RECIENTE</span>
              <b>RETENCIÓN 30 DÍAS</b>
            </div>
            <div className="evidence-list">
              {clips.map((clip) => {
                const vehicle = vehicles.find((item) => item.id === clip.vehicle_id);
                return (
                  <article className={`evidence-item evidence-item--${clip.status}`} key={clip.id}>
                    <span className="evidence-item__preview">
                      <Camera size={20} />
                      <small>{String(clip.metadata_json.channel || "video").toUpperCase()}</small>
                    </span>
                    <span>
                      <small>{vehicle?.unit_code || `UNIDAD ${clip.vehicle_id}`} · EVT-{clip.alert_id}</small>
                      <strong>{clip.event_type.replaceAll(":", " · ")}</strong>
                      <em>
                        <Clock3 size={11} /> {clip.pre_event_seconds}s antes · {clip.post_event_seconds}s después
                      </em>
                    </span>
                    <span className="evidence-item__state">
                      {clip.locked && <LockKeyhole size={12} />}
                      <b>{statusLabel(clip.status)}</b>
                      <small>{new Date(clip.event_at).toLocaleString("es-MX")}</small>
                    </span>
                  </article>
                );
              })}
              {!clips.length && <p className="maintenance-empty">SIN EVIDENCIA GENERADA</p>}
            </div>
          </section>
          <aside className="evidence-policy">
            <ShieldCheck size={24} />
            <strong>POLÍTICA AUTOMÁTICA</strong>
            <p>Pánico, DSM, rutas, zonas de riesgo y alertas críticas solicitan clips sin intervención del operador.</p>
            <dl>
              <div><dt>VENTANA</dt><dd>20s + 40s</dd></div>
              <div><dt>RETENCIÓN</dt><dd>30 días</dd></div>
              <div><dt>ALMACÉN</dt><dd>Proveedor / S3</dd></div>
            </dl>
          </aside>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
